import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/api";
import { SEMINAR } from "@/seminars";
import { StatsCards } from "@/components/admin/StatsCards";
import { Overview } from "@/components/admin/Overview";
import { RegistrationsTable } from "@/components/admin/RegistrationsTable";
import { LinksManager } from "@/components/admin/LinksManager";
import { WaitlistTable } from "@/components/admin/WaitlistTable";
import { GuideDownloads } from "@/components/admin/GuideDownloads";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    document.title = `Dashboard · ${SEMINAR.siteName}`;
  }, []);

  const stats = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await adminApi.get("/admin/stats")).data,
    enabled: Boolean(user),
    refetchInterval: 60_000,
  });

  if (user === null) {
    return <div className="dark admin-shell flex min-h-screen items-center justify-center text-sm text-white/60">Checking session…</div>;
  }
  if (user === false) return <Navigate to="/dashboard" replace />;

  return (
    <div className="dark admin-shell text-foreground" data-testid="admin-dashboard">
      <header className="relative z-10 border-b border-white/10 bg-[#07130f]/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <div className="flex items-center gap-4">
            <img src="/affinity-logo.png" alt="Affinity, A Group Representing Infinity FA" className="h-14 w-auto" data-testid="admin-logo" />
            <div className="hidden leading-tight sm:block">
              <div className="admin-kicker">{SEMINAR.admin.kicker}</div>
              <div className="text-[11px] text-white/60">{SEMINAR.admin.subtitle}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <GuideDownloads />
            <a href="/" target="_blank" rel="noreferrer" className="hidden items-center gap-1.5 text-xs font-semibold text-white/60 transition-colors hover:text-white sm:inline-flex" data-testid="admin-view-site-link">
              View landing page <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <span className="hidden text-xs text-white/50 md:inline" data-testid="admin-user-email">{user.email}</span>
            <Button variant="outline" size="sm" onClick={logout} className="rounded-full border-white/15 bg-white/5 text-xs hover:bg-white/10" data-testid="admin-logout-btn">
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-5 py-8 sm:px-8 sm:py-10">
        <div className="mb-8">
          <p className="admin-kicker mb-2">{SEMINAR.admin.kicker}</p>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Registrations at a glance</h1>
        </div>

        <StatsCards stats={stats.data} loading={stats.isLoading} />

        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList className="h-auto rounded-full bg-white/5 p-1">
            <TabsTrigger value="overview" className="rounded-full px-5 py-2 text-xs font-bold data-[state=active]:bg-[#f2d28f] data-[state=active]:text-[#07130f]" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="registrations" className="rounded-full px-5 py-2 text-xs font-bold data-[state=active]:bg-[#f2d28f] data-[state=active]:text-[#07130f]" data-testid="tab-registrations">Registrations</TabsTrigger>
            <TabsTrigger value="links" className="rounded-full px-5 py-2 text-xs font-bold data-[state=active]:bg-[#f2d28f] data-[state=active]:text-[#07130f]" data-testid="tab-links">Referral links</TabsTrigger>
            <TabsTrigger value="waitlist" className="rounded-full px-5 py-2 text-xs font-bold data-[state=active]:bg-[#f2d28f] data-[state=active]:text-[#07130f]" data-testid="tab-waitlist">Waitlist</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-6">
            <Overview stats={stats.data} />
          </TabsContent>
          <TabsContent value="registrations" className="mt-6">
            <RegistrationsTable onChanged={() => stats.refetch()} />
          </TabsContent>
          <TabsContent value="links" className="mt-6">
            <LinksManager onChanged={() => stats.refetch()} />
          </TabsContent>
          <TabsContent value="waitlist" className="mt-6">
            <WaitlistTable />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
